import React, { useState, useEffect } from 'react';
import { customerService, orderService, repairService, oldGoldService, dashboardService } from '../services/api';
import { GlassPanel, GlassCard, GlassButton, PageHeader, LoadingSpinner } from '../components/GlassUI';
import StatusBadge from '../components/StatusBadge';
import { useAuthStore } from '../store';
import { hasPlanFeature, PLAN_FEATURES } from '../config/plans';
import { SEGMENT_MAP } from '../constants/segments';
import { toCsv, downloadCsv, formatDate, todayStamp } from '../utils/download';
import {
  FaUsers, FaBox, FaTools, FaRupeeSign, FaExclamationTriangle,
  FaPlus, FaShoppingCart, FaBoxOpen, FaStar, FaBirthdayCake,
  FaClock, FaArrowRight, FaUser, FaChartLine, FaDownload, FaGift
} from 'react-icons/fa';

interface DashboardPageProps {
  onNavigate?: (page: string, id?: string) => void;
}

interface SegmentationBucket {
  key: string;
  label: string;
  count: number;
  bgClass: string;
  badgeClass: string;
  icon: React.ReactNode;
}const STAGE_LABELS: Record<string, string> = {
  STAGE_1_PLACEMENT: 'Placed',
  STAGE_2_KARIGAR: 'Karigar',
  STAGE_3_QC: 'QC',
  STAGE_4_LEDGER: 'Ledger',
  STAGE_5_DELIVERY: 'Delivered',
};

const STAGE_DOT_COLORS: Record<string, string> = {
  STAGE_1_PLACEMENT: 'bg-blue-500',
  STAGE_2_KARIGAR: 'bg-yellow-500',
  STAGE_3_QC: 'bg-purple-500',
  STAGE_4_LEDGER: 'bg-orange-500',
  STAGE_5_DELIVERY: 'bg-green-500',
};

export const DashboardPage: React.FC<DashboardPageProps> = ({ onNavigate }) => {
  const { subscription, user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalOrders: 0,
    totalRepairs: 0,
    oldGoldExchanges: 0,
  });
  const [segmentation, setSegmentation] = useState<SegmentationBucket[]>([]);
  const [ordersByStage, setOrdersByStage] = useState<Record<string, number>>({});
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [recentRepairs, setRecentRepairs] = useState<any[]>([]);
  const [reminders, setReminders] = useState<{
    celebrations: any[];
    followUps: any[];
    notPurchased1Year: any[];
    counts: { celebrations: number; followUps: number; notPurchased1Year: number };
  }>({
    celebrations: [],
    followUps: [],
    notPurchased1Year: [],
    counts: { celebrations: 0, followUps: 0, notPurchased1Year: 0 },
  });

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const [custRes, ordersRes, repairsRes, oldGoldRes, segRes, remRes] = await Promise.allSettled([
        customerService.getAll(1, 1),
        orderService.getAll(undefined, 1, 100),
        repairService.getAll(undefined, undefined, 1, 100),
        oldGoldService.getAll(1, 1),
        dashboardService.getSegmentation(),
        dashboardService.getReminders(),
      ]);

      if (custRes.status === 'fulfilled') {
        setStats(prev => ({ ...prev, totalCustomers: custRes.value.data.pagination?.total || 0 }));
      }
      if (ordersRes.status === 'fulfilled') {
        const orderData = ordersRes.value.data.orders || [];
        setStats(prev => ({ ...prev, totalOrders: ordersRes.value.data.pagination?.total || orderData.length }));
        setRecentOrders(orderData.slice(-5).reverse());
        const stageCounts: Record<string, number> = {};
        orderData.forEach((o: any) => {
          const rawStage = o.currentStage || 'STAGE_1_PLACEMENT';
          const stage = rawStage === 'STAGE_5_DELIVERED' ? 'STAGE_5_DELIVERY' : rawStage;
          stageCounts[stage] = (stageCounts[stage] || 0) + 1;
        });
        setOrdersByStage(stageCounts);
      }
      if (repairsRes.status === 'fulfilled') {
        const repairData = repairsRes.value.data.repairs || [];
        setStats(prev => ({ ...prev, totalRepairs: repairsRes.value.data.pagination?.total || repairData.length }));
        setRecentRepairs(repairData.slice(-5).reverse());
      }
      if (oldGoldRes.status === 'fulfilled') {
        setStats(prev => ({ ...prev, oldGoldExchanges: oldGoldRes.value.data.pagination?.total || 0 }));
      }
      if (segRes.status === 'fulfilled') {
        const seg = segRes.value.data || {};
        const bucket = (key: string, icon: React.ReactNode): SegmentationBucket => {
          const meta = SEGMENT_MAP[key];
          return {
            key,
            label: meta?.label || key,
            count: seg[key] || 0,
            bgClass: meta?.bgClass || 'bg-slate-50 border-slate-200',
            badgeClass: meta?.badgeClass || 'bg-slate-100 text-slate-800',
            icon,
          };
        };
        setSegmentation([
          bucket('NPC', <FaUser className="text-amber-500" />),
          bucket('dailyCelebrations', <FaBirthdayCake className="text-pink-500" />),
          bucket('notPurchased1Year', <FaClock className="text-orange-500" />),
          bucket('inactive', <FaClock className="text-gray-500" />),
          bucket('idle', <FaExclamationTriangle className="text-red-400" />),
          bucket('VIP', <FaStar className="text-yellow-500" />),
          bucket('ideal', <FaGift className="text-emerald-500" />),
        ]);
      }
      if (remRes.status === 'fulfilled') {
        const data = remRes.value.data || {};
        setReminders({
          celebrations: data.celebrations || [],
          followUps: data.followUps || [],
          notPurchased1Year: data.notPurchased1Year || [],
          counts: data.counts || { celebrations: 0, followUps: 0, notPurchased1Year: 0 },
        });
      }
    } catch (err: any) {
      console.error('Dashboard load error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const downloadReminders = (kind: 'celebrations' | 'followUps' | 'notPurchased1Year') => {
    const rows = reminders[kind] || [];
    if (rows.length === 0) return;
    const csv = toCsv(rows, [
      { header: 'Name', value: (r: any) => r.customerName },
      { header: 'Phone', value: (r: any) => r.cellNumber },
      { header: 'WhatsApp', value: (r: any) => r.whatsappNumber },
      { header: 'Celebration', value: (r: any) => r.celebrationType },
      { header: 'Days Since', value: (r: any) => r.daysSinceFollowUp ?? r.daysSincePurchase ?? '' },
      { header: 'Last Purchase', value: (r: any) => formatDate(r.lastPurchaseDate) },
      { header: 'Last Follow-up', value: (r: any) => formatDate(r.lastFollowUp) },
      { header: 'Lifetime Billing', value: (r: any) => r.lifetimeBilling ?? 0 },
    ]);
    downloadCsv(`reminders-${kind}-${todayStamp()}.csv`, csv);
  };

  const renderStatCard = (icon: React.ReactNode, label: string, value: number | string, gradient: string) => (
    <GlassCard className="flex items-center gap-4 hover-lift">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-sm ${gradient}`}>
        {icon}
      </div>
      <div>
        <p className="text-slate-500 text-sm">{label}</p>
        <p className="text-slate-900 text-2xl font-bold">{value}</p>
      </div>
    </GlassCard>
  );

  const renderStageCard = (stage: string, count: number) => (
    <div key={stage} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
      <div className={`w-3 h-3 rounded-full ${STAGE_DOT_COLORS[stage] || 'bg-slate-400'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 truncate">{STAGE_LABELS[stage] || stage}</p>
        <p className="text-lg font-bold text-slate-900">{count}</p>
      </div>
    </div>
  );

  if (loading) {
    return <LoadingSpinner label="Loading dashboard..." className="min-h-[60vh]" />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {error && (
        <GlassCard className="mb-6 bg-red-50 border-red-200">
          <p className="text-red-700 text-sm">{error}</p>
        </GlassCard>
      )}

      {/* Header */}
      <PageHeader
        title="Dashboard"
        subtitle={`Welcome${user?.name ? `, ${user.name}` : ''} — here's your store at a glance`}
        icon={<FaChartLine />}
      />

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {renderStatCard(<FaUsers size={20} />, 'Total Customers', stats.totalCustomers, 'bg-gradient-to-br from-blue-500 to-blue-600')}
        {renderStatCard(<FaBox size={20} />, 'Total Orders', stats.totalOrders, 'bg-gradient-to-br from-indigo-500 to-indigo-600')}
        {renderStatCard(<FaTools size={20} />, 'Total Repairs', stats.totalRepairs, 'bg-gradient-to-br from-purple-500 to-purple-600')}
        {renderStatCard(<FaRupeeSign size={20} />, 'Old Gold Exchanges', stats.oldGoldExchanges, 'bg-gradient-to-br from-amber-500 to-amber-600')}
      </div>

      {/* Near-limit upgrade suggestion */}
      {(() => {
        const max = subscription?.maxCustomers;
        if (!max || max >= 999999) return null;
        const used = stats.totalCustomers;
        const pct = (used / max) * 100;
        if (pct < 70) return null;
        return (
          <GlassCard className="mb-6 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <p className="font-semibold text-amber-900 flex items-center gap-2">
                  <FaExclamationTriangle className="text-amber-500" />
                  You've used {used.toLocaleString()} of {max.toLocaleString()} customers ({Math.round(pct)}%)
                </p>
                <p className="text-sm text-amber-800 mt-0.5">
                  Upgrade to the next plan for your business requirements — no data loss.
                </p>
                <div className="h-1.5 w-full bg-amber-200 rounded-full mt-2">
                  <div className="h-1.5 bg-amber-500 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
              </div>
              <GlassButton
                variant="gold"
                className="text-sm shrink-0"
                onClick={() => onNavigate?.('subscription')}
              >
                Upgrade Plan →
              </GlassButton>
            </div>
          </GlassCard>
        );
      })()}

      {/* Quick Actions (top, per request) */}
      <GlassPanel className="p-5 sm:p-6 mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <FaBoxOpen className="text-amber-500 text-base" /> Quick Actions
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <GlassButton
            variant="primary"
            className="flex flex-col items-center gap-2 py-4 h-auto"
            onClick={() => onNavigate?.('onboarding')}
          >
            <FaPlus size={18} />
            <span className="text-xs">New Customer</span>
          </GlassButton>
          {hasPlanFeature(subscription?.plan, PLAN_FEATURES.SALE) && (
            <GlassButton
              variant="primary"
              className="flex flex-col items-center gap-2 py-4 h-auto"
              onClick={() => onNavigate?.('gold-sale')}
            >
              <FaShoppingCart size={18} />
              <span className="text-xs">New Sale</span>
            </GlassButton>
          )}
          <GlassButton
            variant="primary"
            className="flex flex-col items-center gap-2 py-4 h-auto"
            onClick={() => onNavigate?.('orders')}
          >
            <FaBoxOpen size={18} />
            <span className="text-xs">New Order</span>
          </GlassButton>
          <GlassButton
            variant="primary"
            className="flex flex-col items-center gap-2 py-4 h-auto"
            onClick={() => onNavigate?.('repairs')}
          >
            <FaTools size={18} />
            <span className="text-xs">New Repair</span>
          </GlassButton>
          {hasPlanFeature(subscription?.plan, PLAN_FEATURES.OLD_GOLD) && (
            <GlassButton
              variant="primary"
              className="flex flex-col items-center gap-2 py-4 h-auto"
              onClick={() => onNavigate?.('old-gold')}
            >
              <FaRupeeSign size={18} />
              <span className="text-xs">Old Gold</span>
            </GlassButton>
          )}
        </div>
      </GlassPanel>

      {/* Today's Reminders */}
      <div className="mb-8">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <FaBirthdayCake className="text-pink-500 text-base" /> Today&apos;s Reminders
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {([
            { kind: 'celebrations' as const, title: 'Celebrations Today', icon: <FaBirthdayCake className="text-pink-500" />, empty: 'No birthdays or anniversaries today', segment: 'dailyCelebrations' },
            { kind: 'followUps' as const, title: 'Overdue Follow-ups', icon: <FaExclamationTriangle className="text-red-400" />, empty: 'No overdue follow-ups', segment: 'idle' },
            { kind: 'notPurchased1Year' as const, title: 'No Purchase in 1 Year', icon: <FaClock className="text-orange-500" />, empty: 'Everyone has purchased this year', segment: 'notPurchased1Year' },
          ]).map((card) => {
            const rows = reminders[card.kind] || [];
            return (
              <GlassPanel key={card.kind} className="p-5 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2 text-sm">
                    {card.icon} {card.title}
                  </h3>
                  <span className="text-xs font-semibold text-slate-500">{rows.length}</span>
                </div>
                {rows.length === 0 ? (
                  <p className="text-slate-400 text-sm flex-1 py-4 text-center">{card.empty}</p>
                ) : (
                  <div className="space-y-2 flex-1">
                    {rows.slice(0, 4).map((r: any) => (
                      <div key={r.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="truncate text-slate-700">{r.customerName || 'N/A'}</span>
                        <span className="shrink-0 text-xs text-slate-400">
                          {card.kind === 'celebrations'
                            ? (r.celebrationType === 'BIRTHDAY' ? '🎂 Birthday' : '💍 Anniversary')
                            : card.kind === 'followUps'
                              ? `${r.daysSinceFollowUp}d`
                              : `${Math.round((r.daysSincePurchase || 0) / 30)}mo`}
                        </span>
                      </div>
                    ))}
                    {rows.length > 4 && (
                      <p className="text-xs text-slate-400 pt-1">+{rows.length - 4} more</p>
                    )}
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <GlassButton
                    variant="secondary"
                    className="flex-1 text-xs flex items-center justify-center gap-1"
                    onClick={() => onNavigate?.(`segment:${card.segment}`)}
                  >
                    View all <FaArrowRight size={10} />
                  </GlassButton>
                  <GlassButton
                    variant="secondary"
                    className="text-xs flex items-center justify-center gap-1"
                    onClick={() => downloadReminders(card.kind)}
                    disabled={rows.length === 0}
                    title="Download CSV"
                  >
                    <FaDownload size={11} />
                  </GlassButton>
                </div>
              </GlassPanel>
            );
          })}
        </div>
      </div>

      {/* Segmentation Buckets */}
      <div className="mb-8">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <FaUsers className="text-amber-500 text-base" /> Customer Segmentation
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {segmentation.slice(0, 3).map((seg) => (
            <GlassCard key={seg.key} className={`${seg.bgClass}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${seg.badgeClass}`}>
                  {seg.icon}
                  {seg.label}
                </span>
              </div>
              <p className="text-3xl font-bold text-slate-900 mb-3">{seg.count}</p>
              <GlassButton
                variant="secondary"
                className="w-full text-sm flex items-center justify-center gap-1"
                onClick={() => onNavigate?.(`segment:${seg.key}`)}
              >
                View <FaArrowRight size={12} />
              </GlassButton>
            </GlassCard>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {segmentation.slice(3).map((seg) => (
            <GlassCard key={seg.key} className={`${seg.bgClass}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${seg.badgeClass}`}>
                  {seg.icon}
                  {seg.label}
                </span>
              </div>
              <p className="text-3xl font-bold text-slate-900 mb-3">{seg.count}</p>
              <GlassButton
                variant="secondary"
                className="w-full text-sm flex items-center justify-center gap-1"
                onClick={() => onNavigate?.(`segment:${seg.key}`)}
              >
                View <FaArrowRight size={12} />
              </GlassButton>
            </GlassCard>
          ))}
        </div>
      </div>

      {/* Orders by Stage */}
      <div className="mb-8">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <FaBoxOpen className="text-amber-500 text-base" /> Orders by Stage
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.keys(STAGE_LABELS).map((stage) =>
            renderStageCard(stage, ordersByStage[stage] || 0)
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <GlassPanel className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Recent Orders</h3>
          {recentOrders.length === 0 ? (
            <p className="text-slate-400 text-sm">No recent orders</p>
          ) : (
            <div className="space-y-3">
              {recentOrders.map((order: any) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100 cursor-pointer hover:bg-slate-100 transition-all"
                  onClick={() => onNavigate?.('order-detail', order.id)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{order.invoiceNumber}</p>
                    <p className="text-xs text-slate-500">{order.customer?.customerName || 'N/A'}</p>
                  </div>
                  <StatusBadge
                    stage={order.currentStage || 'STAGE_1_PLACEMENT'}
                    type="order"
                    karigarDelayed={order.karigarDelayed}
                    className="text-xs shrink-0 ml-2"
                  />
                </div>
              ))}
            </div>
          )}
        </GlassPanel>

        <GlassPanel className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Recent Repairs</h3>
          {recentRepairs.length === 0 ? (
            <p className="text-slate-400 text-sm">No recent repairs</p>
          ) : (
            <div className="space-y-3">
              {recentRepairs.map((repair: any) => (
                <div
                  key={repair.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100 cursor-pointer hover:bg-slate-100 transition-all"
                  onClick={() => onNavigate?.('repairs')}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{repair.repairTicket}</p>
                    <p className="text-xs text-slate-500">{repair.itemDescription || 'N/A'}</p>
                  </div>
                  <StatusBadge
                    stage={repair.currentStage || 'INWARD'}
                    type="repair"
                    className="text-xs shrink-0 ml-2"
                  />
                </div>
              ))}
            </div>
          )}
        </GlassPanel>
      </div>

    </div>
  );
};

export default DashboardPage;
